/**
 * Single source of truth for how an email verification state is displayed on the Admin Users list.
 *
 * The table column, the Verification Status filter and the legend dialog all read from this file so
 * their labels, icons, colours and wording cannot drift apart. Keep this file free of Angular
 * imports so any component can use it.
 *
 * Four display keys map 1:1 to the status values returned by the API (verification_status). Two are
 * derived in the portal only: "verification_required" and "exempt". The API status values and filter
 * semantics are unchanged.
 */

export type VerificationDisplayKey =
  | 'verified'
  | 'unverified'
  | 'reverification_pending'
  | 'expired'
  | 'verification_required'
  | 'exempt';

export interface VerificationDisplay {
  key: VerificationDisplayKey;
  // Visible chip label. The text is always shown so colour is never the only signal.
  label: string;
  // Material icon name.
  icon: string;
  // Material theme colour. null means neutral, styled by the .verification-neutral class.
  color: 'primary' | 'accent' | 'warn' | null;
  // Drives the red "Sign-in blocked" line.
  blocksSignIn: boolean;
  // The underlying API value, or null for the portal derived "exempt" display.
  apiStatus: string | null;
  // Legend wording. A {appName} token is replaced with the app name at render time.
  legend: {
    appears: string;
    signIn: string;
    appliesTo: string;
    clears: string;
  };
}

export const VERIFICATION_DISPLAYS: Record<
  VerificationDisplayKey,
  VerificationDisplay
> = {
  verified: {
    key: 'verified',
    label: 'Verified',
    icon: 'verified',
    color: 'primary',
    blocksSignIn: false,
    apiStatus: 'verified',
    legend: {
      appears:
        'The user has verified their email address and the next verification deadline is still in the future.',
      signIn: 'Can sign in.',
      appliesTo:
        'Regular users. Existing users who were verified automatically when the feature was introduced also show this until their first deadline.',
      clears:
        'When the deadline passes, the user becomes Re-verification pending or Expired until they verify again.'
    }
  },
  unverified: {
    key: 'unverified',
    label: 'Unverified',
    icon: 'gpp_maybe',
    color: 'accent',
    blocksSignIn: false,
    apiStatus: 'unverified',
    legend: {
      appears:
        'The user has not verified yet and either their deadline is still in the future or no deadline has been set.',
      signIn:
        'Can still sign in until the deadline. With no deadline set, sign-in is never blocked.',
      appliesTo: 'Regular users, mostly newly created or imported users.',
      clears:
        'When the user clicks the link in their verification email they become Verified. If the deadline passes first they become Verification required.'
    }
  },
  reverification_pending: {
    key: 'reverification_pending',
    label: 'Re-verification pending',
    icon: 'schedule',
    color: 'warn',
    blocksSignIn: true,
    apiStatus: 'reverification_pending',
    legend: {
      appears:
        'The deadline passed in the last 30 days, a reminder email had been sent, and the user had verified before.',
      signIn: 'Blocked until the user verifies.',
      appliesTo:
        'Regular users who were verified before and did not renew in time.',
      clears:
        'When the user clicks a verification link they become Verified. After 30 days without verifying they become Expired.'
    }
  },
  expired: {
    key: 'expired',
    label: 'Expired',
    icon: 'gpp_bad',
    color: 'warn',
    blocksSignIn: true,
    apiStatus: 'expired',
    legend: {
      appears:
        'The deadline passed and either no reminder was sent or more than 30 days have passed, and the user had verified before.',
      signIn: 'Blocked until the user verifies.',
      appliesTo: 'Regular users who were verified before and did not renew.',
      clears:
        'When the user clicks a verification link they become Verified. Resend Verification in the row menu sends a new link.'
    }
  },
  verification_required: {
    key: 'verification_required',
    label: 'Verification required',
    icon: 'mark_email_unread',
    color: 'warn',
    blocksSignIn: true,
    apiStatus: null,
    legend: {
      appears:
        'The user has never verified their email address and the deadline has passed.',
      signIn: 'Blocked until the user verifies.',
      appliesTo:
        'Regular users who never verified, for example self-registered users and imported users who ignored the email. This is a display only label. The filter lists these users under Expired or Re-verification pending.',
      clears:
        'When the user clicks a verification link they become Verified.'
    }
  },
  exempt: {
    key: 'exempt',
    label: 'Exempt',
    icon: 'admin_panel_settings',
    color: null,
    blocksSignIn: false,
    apiStatus: null,
    legend: {
      appears: 'The user is a {appName} support user.',
      signIn:
        'Never blocked by email verification, whatever the underlying status.',
      appliesTo: 'Support (super admin) users only.',
      clears:
        'It does not clear. Hover the status in the table to see the underlying status.'
    }
  }
};

// Order used by the legend.
export const VERIFICATION_LEGEND_ORDER: VerificationDisplayKey[] = [
  'verified',
  'unverified',
  'reverification_pending',
  'expired',
  'verification_required',
  'exempt'
];

// Filter options: only values the API understands (the real statuses), plus "All".
// Labels come from VERIFICATION_DISPLAYS so they cannot drift.
export const VERIFICATION_FILTER_OPTIONS: { value: string; label: string }[] =
  [
    { value: '', label: 'All' },
    ...(
      [
        'verified',
        'unverified',
        'reverification_pending',
        'expired'
      ] as VerificationDisplayKey[]
    ).map((key) => ({ value: key, label: VERIFICATION_DISPLAYS[key].label }))
  ];

/**
 * Work out which display applies to a row returned by v2/admin/users.
 *
 * Support users are always Exempt. A blocked status (deadline passed) for a user who has never
 * verified is shown as Verification required. Everything else follows the API status.
 */
export function resolveVerificationDisplay(user: any): VerificationDisplayKey {
  if (user.is_sitesoft_admin) {
    return 'exempt';
  }
  const status = user.verification_status;
  const blockedStatus = status === 'expired' || status === 'reverification_pending';
  if (blockedStatus && !user.email_verified_at) {
    return 'verification_required';
  }
  return (
    VERIFICATION_DISPLAYS[status as VerificationDisplayKey] &&
    status !== 'exempt' &&
    status !== 'verification_required'
      ? status
      : 'unverified'
  ) as VerificationDisplayKey;
}
